import type { BasicDigitalTwin } from 'src/models/BasicDigitalTwin'
import type { DtdlContent, DtdlInterface } from 'src/models/DtdlInterface'
import type { DtdlPrimitiveSchema, DtdlSchema } from 'src/models/Dtdl'
import type { DtdlProperty } from 'src/models/DtdlProperty'
import type { DtdlComponent } from 'src/models/DtdlComponent'

// TODO: try to use https://quasar.dev/quasar-utils/type-checking-utils

export const isFloat = (n: unknown) => {
  return n === Number(n) && n !== Math.trunc(n as number)
}

export const isInteger = (n: unknown) => {
  return n === Number(n) && n === Math.trunc(n as number)
}

/** Check if numeric */
export const isNumeric = (element: unknown): element is number => {
  // is not primitive
  if (element === Object(element)) return false
  if (typeof element === 'string') return false
  return Number.isFinite(element)
}

/** Check if boolean */
export const isBoolean = (element: unknown): element is boolean => {
  return typeof element === 'boolean'
}

/** Check if string */
export const isString = (element: unknown): element is string => {
  return typeof element === 'string'
}

/** Check if array or object */
export const isArrayOrObject = (val: unknown) => {
  return Object(val) === val
}

/**
 * @returns true when input is an object type that is not null, Array, RegExp, or Date.
 */
export const isObject = <T extends Record<string, unknown>>(input: unknown): input is T => {
  return (
    typeof input === 'object' &&
    input === Object(input) &&
    input !== null &&
    !Array.isArray(input) &&
    !(input instanceof RegExp) &&
    !(input instanceof Date)
  )
}

/** Checks whether the provided object is empty */
export const isEmptyObject = (obj: Record<string, unknown>): boolean => {
  return Object.keys(obj).length === 0
}

export const isDate = (n: unknown): n is string | number | Date => {
  const dateWrapper = new Date(n as string | number | Date)
  if (typeof n === 'string') {
    return (
      typeof n === 'string' && dateWrapper.toString() === n && !Number.isNaN(dateWrapper.getDate())
    )
  } else {
    return !Number.isNaN(dateWrapper.getDate())
  }
}

/** Checks wther the provided object is a digital twin */
export const isDigitalTwin = (
  twin: Record<string, unknown> | undefined
): twin is BasicDigitalTwin =>
  typeof twin?.$dtId === 'string' &&
  typeof (twin as BasicDigitalTwin)?.$metadata?.$model === 'string' &&
  (twin as BasicDigitalTwin).$metadata.$model.length > 0

export const hasInvalidDigitalTwins = (
  twins: (BasicDigitalTwin | { [x: string]: unknown } | undefined)[]
): boolean => twins.length !== twins.filter((t) => isDigitalTwin(t)).length

/**
 * Determines whether the given entity is a DtdlPrimitiveSchema schema type
 * @param value - Any entity
 * @returns true is it is DtdlPrimitiveSchema type, false otherwise.
 */
export function isPrimitiveType(value: unknown) {
  return isPrimitiveSchema(getPrimitiveType(value))
}

/**
 * Determines whether the given value is a DtdlPrimitiveSchema
 * @param value - Any value
 * @returns true is it is DtdlPrimitiveSchema type, false otherwise.
 */
export function isPrimitiveSchema(value: unknown): value is DtdlPrimitiveSchema {
  return !!isString(value as DtdlPrimitiveSchema)
}

/** Checks whether the provided schema is a DtdlNumericSchema */
export const isDtdlNumericSchema = (
  schema: DtdlSchema | undefined
): schema is 'double' | 'integer' | 'long' | 'float' =>
  typeof schema === 'string' && ['double', 'integer', 'long', 'float'].includes(schema)

/** Gets the primitive type of the element or return null*/
export const getPrimitiveType = (element: unknown): DtdlPrimitiveSchema | null => {
  if (undefined === element || element === null) return null
  // TODO: time
  // date and dateTime
  if (element === Object(element) && isDate(element)) return 'dateTime'
  if (typeof element === 'string' && isDate(element)) return 'dateTime'
  // string
  if (typeof element === 'string') return 'string'
  // boolean
  if (typeof element === 'boolean') return 'boolean'
  // float and double
  if (Number.isFinite(element) && isFloat(element)) return 'double'
  // long and integer
  if (Number.isFinite(element) && isInteger(element)) return 'integer'
  // TODO: duration
  return null
}

/** Checks whether the object could be a DTDL Interface (no checks for validity) */
export const isDtdlInterface = (obj: Record<string, unknown>): obj is DtdlInterface =>
  typeof obj['@id'] === 'string' && obj['@type'] === 'Interface'

/** Checks whether the object could be a DTDL Interface (no checks for validity) */
export const isDtdlProperty = (obj: DtdlContent): obj is DtdlProperty =>
  obj['@type'] === 'Property' || obj['@type'].includes('Property')

/** Checks whether the object could be a DTDL Interface (no checks for validity) */
export const isDtdlComponent = (obj: DtdlContent): obj is DtdlComponent =>
  obj['@type'] === 'Component'
